<template>
    <div class="flex-1 flex flex-col min-h-0 border-r border-gray-200 bg-white">
        <div class="flex-1 flex flex-col overflow-y-auto px-2 py-5">
            <GgmsAgencySelect />
            <nav class="flex-1 bg-white space-y-1" aria-label="Sidebar">
                <div
                    v-for="(item, index) in props.items"
                    :to="item.path"
                    :key="item.name"
                    :class="getClassesItem(item)"
                >
                    <GgmsDisclosureForNavigation
                        v-if="item.children"
                        :key="item.name"
                        :ref="(el) => (disclosures[index] = el)"
                        :children-items="item.children"
                        class="flex w-full space-y-1"
                    >
                        <template #header="{ isOpen }">
                            <component
                                :is="item.icon"
                                :class="[getClassesIcon(item), getParentIconClass(isOpen)]"
                                :stroke="getParentIconStroke(isOpen)"
                                aria-hidden="true"
                            />
                            <span class="flex-1 self-center text-sm font-medium" :class="getParentTextClass(isOpen)">
                                {{ item.name }}
                            </span>
                        </template>
                        <template #content>
                            <router-link
                                v-for="child in item.children"
                                :to="child.path"
                                :key="child.name"
                                :class="getClassesChildItem(child)"
                                @click="closeDisclosure(index)"
                            >
                                <span class="flex-1 text-sm font-medium">
                                    {{ child.name }}
                                </span>
                            </router-link>
                        </template>
                    </GgmsDisclosureForNavigation>
                    <router-link
                        v-else
                        :to="getItemPath(item)"
                        class="flex w-full items-center px-2 py-2"
                        @click="closeDisclosure"
                    >
                        <component :is="item.icon" :class="getClassesIcon(item)" aria-hidden="true" />
                        <span class="flex-1 text-sm font-medium">
                            {{ item.name }}
                        </span>
                    </router-link>
                </div>
            </nav>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { ref } from "vue"
import { useRoute } from "vue-router"
import GgmsDisclosureForNavigation from "@/components/GgmsDisclosureForNavigation.vue"
import GgmsAgencySelect from "@/components/GgmsAgencySelect.vue"
import { useNavigationSidebarStore } from "@/stores/navigation-sidebar"
import { NavigationChildItem, NavigationItem } from "@/shared/models/navigation"

const route = useRoute()

const navigationSidebarStore = useNavigationSidebarStore()

interface Props {
    items: NavigationItem[]
}

const props = defineProps<Props>()

const disclosures = ref([] as any[])

function getParentIconClass(isOpen: boolean) {
    return isOpen ? "stroke-primary-color-600" : "stroke-gray-400 group-hover:stroke-gray-500"
}

function getParentIconStroke(isOpen: boolean) {
    return isOpen ? "stroke-primary-color-600" : "stroke-gray-400 group-hover:stroke-gray-500"
}

function getParentTextClass(isOpen: boolean) {
    return isOpen ? "text-primary-color-600" : ""
}

function getClassesItem(item: NavigationItem) {
    return [
        checkActiveRoute(item.path as string)
            ? "bg-primary-color-600 text-white hover:bg-primary-color-700"
            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50",
        "group flex items-center text-sm font-medium rounded-md",
    ]
}

function getClassesChildItem(item: NavigationChildItem) {
    return [
        checkActiveRoute(item.path as string)
            ? "bg-primary-color-600 text-white hover:bg-primary-color-700"
            : "text-gray-600 hover:text-gray-900 hover:bg-gray-50",
        "group flex items-center px-10 py-2 text-sm font-medium rounded-md",
    ]
}

function getClassesIcon(item: NavigationItem) {
    return [
        checkActiveRoute(item.path as string) ? "text-white" : "text-gray-400 group-hover:text-gray-500",
        "mr-3 flex-shrink-0 h-6 w-6",
    ]
}

function checkActiveRoute(path: string) {
    return route.path === path || route.path.includes(path)
}

function closeDisclosure(index?: number) {
    navigationSidebarStore.toggleSidebar(false)
    disclosures.value.forEach((disclosure, disclosureIndex) => {
        if (disclosureIndex === index) {
            return
        }
        disclosure?.closeDisclosure()
    })
}

function getItemPath(item: NavigationItem) {
    return item?.path || ""
}
</script>
